import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  Checkbox,
  InputAdornment
} from "@material-ui/core";
import { Formik } from "formik";
import DateFnsUtils from "@date-io/date-fns";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ptBrLocale from "date-fns/locale/pt-BR";
import enUsLocale from "date-fns/locale/en-US";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import Dialog from "../../waybee-ui/Dialog/Dialog";
import Heading from "../../waybee-ui/Heading/Heading";
import Button from "../../waybee-ui/Button";
import ConfirmAlert from "../../waybee-ui/ConfirmAlert";
import TeacherService from "../../services/TeacherService";
import decodeBase64 from "../../utils/decodeBase64";
import { AbilityContext } from "../../acl/can";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import {
  canAdvancedOccurrence,
  splitOccurrenceNotes,
  joinOccurrenceNotes
} from "../../utils/occurrenceAdvanced";
import OccurrenceNotesFields from "./OccurrenceNotesFields";
import OccurrenceContextFields from "./OccurrenceContextFields";
import OccurrenceAttachmentFields from "./OccurrenceAttachmentFields";
import OccurrenceAvailableWebField from "./OccurrenceAvailableWebField";
import { displayOccurrenceContext } from "./occurrenceDisplay";
import {
  filterAllowedAttachments,
  notifyRejectedAttachment
} from "../../utils/occurrenceAttachmentRules";

const FormGrid = styled(Grid)`
  overflow: scroll;

  @media (min-height: 800px) {
    scrollbar-width: none;
  }
`;

const Content = styled(DialogContent)`
  overflow: hidden;
`;

const AcknowledgeCheckbox = styled(Checkbox)`
  padding: 0;
`;

const AcknowledgeTextField = styled(TextField)`
  .MuiInput-underline:before {
    border-bottom: none;
  }
  .MuiInput-underline:after {
    border-bottom: none;
  }
  .MuiInput-underline:hover:not(.Mui-disabled):before {
    border-bottom: none;
  }
`;

const OccurrenceDialog = ({
  open,
  handleClose,
  occurrence,
  studentId,
  classId
}) => {
  const { t, i18n } = useTranslation();
  const alert = useAlert();
  const ability = useContext(AbilityContext);
  const isAdvanced = !!(
    ability && ability.can("use module", "diaryOccurrenceAdvanced")
  );
  const canEdit = !!(
    occurrence.editable &&
    (isAdvanced
      ? canAdvancedOccurrence(ability, "edit")
      : ability && ability.can("save", "diaryOccurrences"))
  );
  const canDelete = !!(
    occurrence.editable &&
    (isAdvanced
      ? canAdvancedOccurrence(ability, "delete")
      : ability && ability.can("save", "diaryOccurrences"))
  );
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [occurFiles, setOccurFiles] = useState([]);
  const [savedAttachmentIds, setSavedAttachmentIds] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentsLoadFailed, setAttachmentsLoadFailed] = useState(false);

  useEffect(() => {
    if (!open || !(occurrence.attachmentsCount > 0)) return undefined;
    let active = true;
    setLoadingAttachments(true);
    setAttachmentsLoadFailed(false);
    TeacherService.occurrenceAttachments(occurrence.id)
      .then(({ occurAttachment = [] }) => {
        if (!active) return;
        setOccurFiles(
          occurAttachment.map(attachment => ({
            attachmentId: attachment.attachmentId,
            attachmentName: attachment.attachmentName,
            attachmentContent: attachment.attachmentData
          }))
        );
        setSavedAttachmentIds(
          occurAttachment.map(attachment => attachment.attachmentId)
        );
      })
      .catch(e => {
        console.error(e);
        if (!active) return;
        setAttachmentsLoadFailed(true);
        alert.show(t("occurrences.dialog.attachmentsLoadError"), {
          title: t("common.actions.error")
        });
      })
      .finally(() => {
        if (active) setLoadingAttachments(false);
      });
    return () => {
      active = false;
    };
  }, [open, occurrence.id, occurrence.attachmentsCount]);
  const dateLocale = (i18n.language || "pt-BR").startsWith("en")
    ? "en-US"
    : "pt-BR";
  const pickerLocale = dateLocale === "en-US" ? enUsLocale : ptBrLocale;
  const stringDate = new Date(occurrence.date)
    .toLocaleString(dateLocale)
    .split(",");
  const splitNotes = splitOccurrenceNotes(occurrence.description);
  const formatData = (data, deleteOccurrence) => {
    const removedOccurrence = {
      description: occurrence.description,
      id: `${occurrence.idType}-${occurrence.idGroup}`,
      idOccur: occurrence.id,
      studentId: occurrence.studentId
    };
    const base = {
      absences: [],
      idTurmaDisc: occurrence.idTurma,
      idPerlet: occurrence.idPerlet,
      date: moment(data.date).toISOString()
    };

    if (deleteOccurrence) {
      return { ...base, occurrences: [], removed: [removedOccurrence] };
    }

    const keptIds = occurFiles.map(file => file.attachmentId);
    const removedSavedAttachment = savedAttachmentIds.some(
      id => !keptIds.includes(id)
    );
    const editInPlace = !removedSavedAttachment;

    const files = occurFiles.map(occurFile => ({
      attachmentId: occurFile.attachmentId,
      name: occurFile.attachmentName,
      content:
        editInPlace && occurFile.attachmentId != null
          ? undefined
          : occurFile.attachmentContent
    }));

    return {
      ...base,
      occurrences: [
        {
          occurrenceId: editInPlace ? occurrence.id : undefined,
          description: joinOccurrenceNotes(data.obs, data.internalNotes),
          idGroup: occurrence.idGroup,
          idType: occurrence.idType,
          files,
          studentId,
          availableWeb: false
        }
      ],
      removed: editInPlace ? [] : [removedOccurrence]
    };
  };

  const requestSave = values => {
    setPendingValues(values);
    setConfirmAction("save");
  };

  const requestDelete = values => {
    setPendingValues(values);
    setConfirmAction("delete");
  };

  const closeConfirm = () => {
    setConfirmAction(null);
    setPendingValues(null);
  };

  const insertOccurrence = async (data, deleteOccurrence) => {
    const occurData = formatData(data, deleteOccurrence);
    setLoading(true);
    closeConfirm();
    try {
      await TeacherService.saveOccurrence(occurData, {
        classId,
        filter: {
          course: { id: studentId },
          period: "Individual"
        }
      });
      alert.show(t("common.messages.processingRequest"), {
        title: deleteOccurrence
          ? t("occurrences.dialog.deleted")
          : t("occurrences.dialog.sent")
      });
      fetchGradeJobQueue();
      handleClose(true);
    } catch (e) {
      console.error(e);
      const message = _.get(
        e,
        "response.data.error.message",
        t("occurrences.dialog.sendError")
      );
      alert.show(message, { title: t("common.actions.error") });
    } finally {
      setLoading(false);
    }
  };

  const confirmPendingAction = () => {
    if (!pendingValues || !confirmAction) return;
    insertOccurrence(pendingValues, confirmAction === "delete");
  };

  const deleteAttachment = occurFile => {
    setOccurFiles(files => files.filter(file => file !== occurFile));
  };

  const handleFileSelect = e => {
    filterAllowedAttachments(
      e.target.files,
      notifyRejectedAttachment(alert, t)
    ).forEach(file => {
      const reader = new FileReader();
      reader.onload = event => {
        setOccurFiles(current => [
          ...current,
          {
            attachmentName: file.name,
            attachmentContent: event.target.result.split(",")[1]
          }
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleOnOpenFile = file => {
    const occurrenceBase64 = file.attachmentContent;
    const decodedUrl = decodeBase64(occurrenceBase64);

    const downloadLink = document.createElement("a");
    downloadLink.href = decodedUrl;
    downloadLink.download = file.attachmentName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth closeButton>
        <DialogTitle>{t("occurrences.dialog.title")}</DialogTitle>
        <Formik
          initialValues={{
            obs: splitNotes.notes,
            internalNotes: splitNotes.internalNotes,
            type: occurrence.reason,
            stage: occurrence.stage,
            teacher: displayOccurrenceContext(occurrence.teacher),
            course: displayOccurrenceContext(occurrence.courseName),
            date: occurrence.date,
            responsibleAcknowledge: occurrence.responsibleAcknowledge,
            responsibleName: occurrence.responsibleName
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <FormGrid>
              <Content>
                <Heading>
                  {t("occurrences.dialog.ofDay", {
                    date: stringDate[0],
                    time: (stringDate[1] || "").trim(),
                    stage: occurrence.stage
                  })}
                </Heading>
                <Grid container>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <AcknowledgeTextField
                        name="responsibleAcknowledge"
                        id="responsibleAcknowledge"
                        label={t("occurrences.dialog.aware")}
                        value={
                          values.responsibleAcknowledge === "S"
                            ? values.responsibleName
                            : t("occurrences.dialog.noAck")
                        }
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AcknowledgeCheckbox
                                checked={values.responsibleAcknowledge === "S"}
                                disabled
                              />
                            </InputAdornment>
                          ),
                          style: {
                            opacity: 0.5,
                            pointerEvents: "none"
                          }
                        }}
                      />
                    </Grid>
                    <OccurrenceAvailableWebField
                      asGrid
                      checked={values.availableWeb}
                    />
                    <OccurrenceContextFields
                      teacher={values.teacher}
                      course={values.course}
                      stage={values.stage}
                      type={values.type}
                    />
                    <Grid item xs={12}>
                      <MuiPickersUtilsProvider
                        utils={DateFnsUtils}
                        locale={pickerLocale}
                      >
                        <DatePicker
                          name="date"
                          id="date"
                          label={t("common.labels.date")}
                          value={values.date}
                          fullWidth
                          format="dd/MM/yyyy"
                          onChange={value => {
                            setFieldValue("date", value);
                          }}
                          disabled={!canEdit}
                        />
                      </MuiPickersUtilsProvider>
                    </Grid>
                    <OccurrenceNotesFields
                      notesName="obs"
                      notesValue={values.obs}
                      onChange={handleChange}
                      disabled={!canEdit}
                      asGrid
                    />

                    <Grid item xs={12}>
                      <OccurrenceAttachmentFields
                        files={occurFiles}
                        canAdd={canEdit}
                        onSelect={handleFileSelect}
                        onDelete={canEdit ? deleteAttachment : undefined}
                        onOpen={handleOnOpenFile}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Content>
              <DialogActions>
                {loading || loadingAttachments ? (
                  <CircularProgress />
                ) : (
                  <>
                    {canDelete && (
                      <Button
                        variant="contained"
                        color="default"
                        onClick={() => requestDelete(values)}
                      >
                        {t("common.actions.delete")}
                      </Button>
                    )}
                    {canEdit ? (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={attachmentsLoadFailed}
                        onClick={() => requestSave(values)}
                      >
                        {t("common.actions.save")}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClose}
                      >
                        {t("common.actions.ok")}
                      </Button>
                    )}
                  </>
                )}
              </DialogActions>
            </FormGrid>
          )}
        </Formik>
      </Dialog>
      <ConfirmAlert
        open={!!confirmAction}
        onClose={closeConfirm}
        onConfirm={confirmPendingAction}
        title={
          confirmAction === "delete"
            ? t("occurrences.dialog.deleteConfirmTitle")
            : t("occurrences.dialog.saveConfirmTitle")
        }
        text={
          confirmAction === "delete"
            ? t("occurrences.dialog.deleteConfirm")
            : t("occurrences.dialog.saveEditConfirm")
        }
        confirmButtonText={
          confirmAction === "delete"
            ? t("common.actions.delete")
            : t("common.actions.save")
        }
        confirmButtonColor={confirmAction === "delete" ? "error" : "primary"}
        cancelButtonText={t("common.actions.cancel")}
      />
    </>
  );
};

OccurrenceDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  occurrence: PropTypes.shape({
    courseName: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    stage: PropTypes.string.isRequired,
    teacher: PropTypes.string.isRequired,
    editable: PropTypes.bool,
    idType: PropTypes.string.isRequired,
    idGroup: PropTypes.string.isRequired,
    idPerlet: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    idTurma: PropTypes.string.isRequired,
    studentId: PropTypes.string,
    attachmentsCount: PropTypes.number,
    responsibleAcknowledge: PropTypes.string,
    responsibleName: PropTypes.string
  }).isRequired,
  studentId: PropTypes.string.isRequired,
  classId: PropTypes.string,
  open: PropTypes.bool.isRequired
};

OccurrenceDialog.defaultProps = {
  classId: undefined
};

export default OccurrenceDialog;
